import { useState } from 'react';
import { X, FloppyDisk, Spinner, Plus, Trash } from '@phosphor-icons/react';
import { API, PROF_COLORS, CATEGORIA_CONFIG } from './constants';

export const ProfissionaisModal = ({ profissionais, onClose, onRefresh }) => {
  const [form, setForm] = useState({
    nome: '',
    categoria: 'profissional',
    especialidades: '',
    cor: PROF_COLORS[profissionais.length % PROF_COLORS.length],
  });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return;
    setSaving(true);
    try {
      const body = {
        nome: form.nome,
        categoria: form.categoria,
        especialidades: form.especialidades.split(',').map(s => s.trim()).filter(Boolean),
        cor: form.cor,
        ativo: true,
      };
      const url = editId ? `${API}/cod/profissionais/${editId}` : `${API}/cod/profissionais`;
      const method = editId ? 'PUT' : 'POST';
      await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      setForm({ nome: '', categoria: 'profissional', especialidades: '', cor: PROF_COLORS[(profissionais.length + 1) % PROF_COLORS.length] });
      setEditId(null);
      onRefresh();
    } catch { /* */ } finally { setSaving(false); }
  };

  const handleEdit = (p) => {
    setEditId(p.id);
    setForm({ nome: p.nome, categoria: p.categoria || 'profissional', especialidades: (p.especialidades || []).join(', '), cor: p.cor || PROF_COLORS[0] });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir?')) return;
    await fetch(`${API}/cod/profissionais/${id}`, { method: 'DELETE' });
    onRefresh();
  };

  const grouped = {
    profissional: profissionais.filter(p => (p.categoria || 'profissional') === 'profissional'),
    auxiliar: profissionais.filter(p => p.categoria === 'auxiliar'),
    estagiario: profissionais.filter(p => p.categoria === 'estagiario'),
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="profissionais-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <h2 className="text-xl font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Equipe</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 pb-2">
          {Object.entries(grouped).map(([cat, list]) => (
            list.length > 0 && (
              <div key={cat} className="mb-4">
                <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide mb-2">{CATEGORIA_CONFIG[cat]?.label}</p>
                <div className="space-y-1.5">
                  {list.map(p => (
                    <div key={p.id} data-testid={`prof-${p.id}`} className="flex items-center gap-3 p-2.5 rounded-xl bg-[#F7F6F4]">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" style={{ backgroundColor: p.cor || '#1B4332' }}>
                        {p.nome?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#18181B] truncate">{p.nome}</p>
                      </div>
                      <button onClick={() => handleEdit(p)} className="text-xs text-[#71717A] hover:text-[#18181B] px-2 py-1 rounded-lg hover:bg-white">Editar</button>
                      <button data-testid={`btn-delete-prof-${p.id}`} onClick={() => handleDelete(p.id)} className="text-[#A1A1AA] hover:text-red-500">
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
          {profissionais.length === 0 && <p className="text-sm text-[#A1A1AA] text-center py-4">Nenhum membro cadastrado</p>}
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-3 border-t border-[#E4E4E7] pt-4">
          <div className="flex items-center gap-2 mb-1">
            <Plus size={14} weight="bold" className="text-[#1B4332]" />
            <span className="text-xs font-semibold text-[#18181B] uppercase tracking-wide">{editId ? 'Editar' : 'Adicionar'}</span>
          </div>
          <input data-testid="input-prof-nome" type="text" value={form.nome}
            onChange={(e) => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Nome"
            className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
          />
          <div className="flex gap-2">
            <select data-testid="select-categoria" value={form.categoria}
              onChange={(e) => setForm(p => ({ ...p, categoria: e.target.value }))}
              className="flex-1 px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
            >
              <option value="profissional">Profissional</option>
              <option value="auxiliar">Auxiliar</option>
              <option value="estagiario">Estagiario</option>
            </select>
            <div className="flex items-center gap-1 px-2">
              {PROF_COLORS.slice(0, 5).map(c => (
                <button key={c} type="button" onClick={() => setForm(p => ({ ...p, cor: c }))}
                  className={`w-5 h-5 rounded-full transition-all ${form.cor === c ? 'ring-2 ring-offset-1 ring-[#18181B]' : 'hover:scale-110'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <button type="submit" disabled={saving || !form.nome.trim()} data-testid="btn-salvar-prof"
            className="w-full py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 transition-all hover:shadow-md disabled:opacity-60"
            style={{ backgroundColor: '#1B4332' }}
          >
            {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
            {editId ? 'Salvar' : 'Adicionar'}
          </button>
          {editId && (
            <button type="button" onClick={() => { setEditId(null); setForm({ nome: '', categoria: 'profissional', especialidades: '', cor: PROF_COLORS[0] }); }}
              className="w-full py-2 text-xs text-[#71717A] hover:text-[#18181B]"
            >Cancelar edicao</button>
          )}
        </form>
      </div>
    </div>
  );
};

export default ProfissionaisModal;
